/**
 * `/api/admin/people` — the WayAI team's moderation of person-to-person messaging (App Store
 * guideline 1.2: act on a report of user-generated content within 24 hours, removing the
 * content and ejecting the person who posted it). Platform admins only, through the audited
 * `wayai admin people` CLI.
 *
 * - The team reads the reports people file (`people-reports.ts`), newest first, each with the
 *   actions recorded against it; `pending` keeps the reports with none.
 * - `remove_message`: a message in a reported thread or community is deleted for everyone, as its
 *   author's own delete would delete it: its text and file go, and every member who saw it sees
 *   the tombstone a deleted message leaves.
 * - `suspend` / `unsuspend`: a suspended account keeps reading, and keeps what only takes its
 *   own presence away — deleting its messages, leaving, declining, cancelling its invites,
 *   removing a contact, blocking, reporting — but reaches no one: it cannot send a message,
 *   invite, accept an invite, create a group or an org-linked 1:1, or change a group (its name,
 *   avatar, admins or members); in a community (gh #4872 Decision 9) it cannot post, edit, mark an
 *   answer, join, moderate or create a room. Each refusal answers it alone, 409 with `details.reason`
 *   `PEOPLE_SUSPENDED_REASON`; nobody else is told.
 * - `dismiss`: a report reviewed with nothing to remove or suspend.
 *
 * Every action is recorded once it is done — who, what, when, and the report it answers — in
 * one log the team reads with the report and the account.
 */

import { z } from 'zod';
import { optionalTrimmedText, timestampSchema, uuidSchema } from './common';
import { peopleContactUserIdParam } from './people-contacts';
import type { PeopleReportEvidence, PeopleReportReason, PeopleReportTarget } from './people-reports';

// =============================================================================
// LIMITS
// =============================================================================

/** The longest note an admin records with an action, in characters. */
export const PEOPLE_MODERATION_NOTE_MAX_LENGTH = 500;
/** Reports per page of the admins' list. */
export const ADMIN_PEOPLE_REPORTS_PAGE_MAX = 100;
export const ADMIN_PEOPLE_REPORTS_PAGE_DEFAULT = 50;
/** The recorded actions a report's or an account's read returns, newest first. */
export const ADMIN_PEOPLE_ACTIONS_MAX = 50;

/**
 * The `details.reason` of every refusal a suspended account gets on the people surface (409).
 * A client that predates it shows its generic error.
 */
export const PEOPLE_SUSPENDED_REASON = 'suspended';

// =============================================================================
// REQUEST
// =============================================================================

export const peopleModerationActionSchema = z.enum(['remove_message', 'suspend', 'unsuspend', 'dismiss']);
export type PeopleModerationAction = z.infer<typeof peopleModerationActionSchema>;

/** An admin's note on an action, optional: trimmed, and absent when nothing is left. */
const moderationNoteSchema = optionalTrimmedText(PEOPLE_MODERATION_NOTE_MAX_LENGTH);

/** GET /api/admin/people/reports — newest first; `pending` keeps reports with no action yet. */
export const adminPeopleReportListQuery = z.object({
  pending: z.enum(['true', 'false']).optional().transform((value) => value === 'true'),
  /** Lower bound on `created_at`. */
  since: timestampSchema.optional(),
  limit: z.coerce.number().int().min(1).max(ADMIN_PEOPLE_REPORTS_PAGE_MAX).default(ADMIN_PEOPLE_REPORTS_PAGE_DEFAULT),
  offset: z.coerce.number().int().min(0).default(0),
});

/** GET /api/admin/people/reports/:reportId, POST …/:reportId/dismiss */
export const adminPeopleReportIdParam = z.object({ reportId: uuidSchema });

/** GET /api/admin/people/users/:userId, POST …/:userId/suspend, POST …/:userId/unsuspend */
export const adminPeopleUserIdParam = peopleContactUserIdParam;

/**
 * POST /api/admin/people/message-removals — a message in the thread or community `report_id`
 * names: `message_id`, or the report's own reported message when it is omitted.
 */
export const adminPeopleMessageRemovalBody = z
  .object({ report_id: uuidSchema, message_id: uuidSchema.optional(), note: moderationNoteSchema })
  .strict();

/** POST /api/admin/people/users/:userId/suspend — `report_id` is the report it answers, if any. */
export const adminPeopleSuspendBody = z
  .object({ report_id: uuidSchema.optional(), note: moderationNoteSchema })
  .strict();

/** POST /api/admin/people/users/:userId/unsuspend, POST /api/admin/people/reports/:reportId/dismiss */
export const adminPeopleNoteBody = z.object({ note: moderationNoteSchema }).strict();

export type AdminPeopleReportListQuery = z.input<typeof adminPeopleReportListQuery>;
export type AdminPeopleMessageRemovalBody = z.input<typeof adminPeopleMessageRemovalBody>;
export type AdminPeopleSuspendBody = z.input<typeof adminPeopleSuspendBody>;
export type AdminPeopleNoteBody = z.input<typeof adminPeopleNoteBody>;

// =============================================================================
// RESPONSE
// =============================================================================

/** One recorded action: who did what, when, and to which report, account, thread or community, and message. */
export interface PeopleModerationRecord {
  action_id: string;
  action: PeopleModerationAction;
  /** The platform admin who acted. */
  admin_user_id: string;
  /** The report it answers; null for an action taken without one. */
  report_id: string | null;
  /** The account acted on — the suspended one, or the removed message's author; null for a dismissal. */
  user_id: string | null;
  thread_id: string | null;
  /** A community message's removal: its community (gh #4872); null otherwise. */
  community_id: string | null;
  message_id: string | null;
  note: string | null;
  created_at: string;
}

/** A report as the admins' list shows it: ids, enums and times — never its text. */
export interface AdminPeopleReportSummary {
  report_id: string;
  created_at: string;
  reporter_user_id: string;
  target: PeopleReportTarget;
  reason: PeopleReportReason;
  thread_id: string | null;
  /** A community report's community (gh #4872); null otherwise. */
  community_id: string | null;
  message_id: string | null;
  /** The person the report is about; null for a group's own report. */
  reported_user_id: string | null;
  /** When the report's email reached the admins; null until it has. */
  notified_at: string | null;
  /** Actions recorded against it; 0 while it is pending. */
  action_count: number;
}

export interface AdminPeopleReportDetail extends AdminPeopleReportSummary {
  /** The reporter's own note. */
  details: string | null;
  /** What the reporter saw, as the report keeps it. */
  evidence: PeopleReportEvidence;
  /** The newest `ADMIN_PEOPLE_ACTIONS_MAX` actions recorded against it, newest first. */
  actions: PeopleModerationRecord[];
}

/** An account's standing on the people surface, and the actions recorded on it. */
export interface AdminPeopleUser {
  user_id: string;
  /** When its current suspension began; null while it is not suspended. */
  suspended_at: string | null;
  /** The newest `ADMIN_PEOPLE_ACTIONS_MAX` actions recorded on it, newest first. */
  actions: PeopleModerationRecord[];
}

/** GET /api/admin/people/reports */
export interface AdminPeopleReportListResponse {
  data: AdminPeopleReportSummary[];
  total: number;
}

/** GET /api/admin/people/reports/:reportId */
export interface AdminPeopleReportResponse {
  data: AdminPeopleReportDetail;
}

/** GET /api/admin/people/users/:userId */
export interface AdminPeopleUserResponse {
  data: AdminPeopleUser;
}

/**
 * Every moderation action's answer: the record it wrote, and whether the action changed
 * anything — false for a message already deleted, a suspension already in place or one already
 * lifted, which are recorded all the same.
 */
export interface AdminPeopleModerationResponse {
  data: { record: PeopleModerationRecord; changed: boolean };
}
